import React, { useState } from 'react';
import { Calendar, Clock, MessageCircle, ArrowRight, Check, Sparkles } from 'lucide-react';
import ServiceDetailModal from '../modals/ServiceDetailModal';
import { useLanguage } from '../../context/LanguageContext';
import { Link } from 'react-router-dom';

const iconMap = {
  Calendar: Calendar,
  Clock: Clock,
  MessageCircle: MessageCircle,
};

// All services data with translations for all languages
const servicesContent = {
  pt: {
    sectionLabel: 'Wedding Planner',
    sectionTitle: 'Serviços de Planeamento',
    sectionSubtitle: 'Cada casamento merece um acompanhamento à sua medida. Escolha o nível de envolvimento que faz sentido para vocês.',
    learnMore: 'Saber mais',
    idealFor: 'Para quem é',
    mostChosen: 'Mais Escolhido',
    services: [
      {
        id: 1,
        title: "Essência",
        packType: "Pack Essencial",
        description: "Para casais que gostam de ter as rédeas do planeamento, mas valorizam a segurança de uma orientação profissional. Um ponto de partida sólido para começarem esta jornada com confiança.",
        icon: "MessageCircle",
        features: [
          { title: "Consultoria inicial personalizada", description: "Uma sessão dedicada a conhecer a vossa visão, responder a dúvidas e traçar o caminho do planeamento." },
          { title: "Orientação geral no planeamento", description: "Linhas orientadoras sobre timings, prioridades e decisões fundamentais para o vosso casamento." },
          { title: "Apoio na organização base", description: "Ferramentas e estrutura para manterem tudo organizado ao longo do processo." },
          { title: "Lista de fornecedores recomendados", description: "Acesso a contactos de confiança adequados ao vosso estilo e orçamento." }
        ],
        ideal: "Ideal para casais autónomos que querem planear ao seu ritmo, mas com a tranquilidade de saber que têm apoio profissional quando precisarem."
      },
      {
        id: 2,
        title: "Harmonia",
        packType: "Pack Intermédio",
        description: "Um acompanhamento mais próximo para casais que desejam partilhar o processo de planeamento. Estarei ao vosso lado nas decisões importantes, garantindo que cada escolha reflete quem são.",
        icon: "Clock",
        features: [
          { title: "Planeamento estruturado e faseado", description: "Organização completa do processo com etapas claras, prazos definidos e acompanhamento regular." },
          { title: "Apoio na seleção de fornecedores", description: "Presença nas reuniões com fornecedores, análise de propostas e orientação nas escolhas." },
          { title: "Gestão de timings e prioridades", description: "Acompanhamento ativo para garantir que nada fica esquecido e tudo acontece no momento certo." },
          { title: "Reuniões de acompanhamento periódicas", description: "Pontos de situação regulares para ajustar detalhes e manter o planeamento no rumo certo." },
          { title: "Apoio próximo nas semanas finais", description: "Presença mais intensa à medida que o grande dia se aproxima, para que cheguem tranquilos ao altar." }
        ],
        ideal: "Ideal para casais que querem estar envolvidos no planeamento, mas preferem ter alguém de confiança a caminhar ao seu lado."
      },
      {
        id: 3,
        title: "Plenitude",
        packType: "Pack Completo",
        description: "A experiência integral de ter uma wedding planner dedicada ao vosso casamento. Do primeiro esboço ao último sorriso da noite, cuido de tudo para que vivam cada momento com o coração leve.",
        icon: "Calendar",
        features: [
          { title: "Planeamento completo do casamento", description: "Gestão integral de todos os elementos, desde a escolha do espaço até aos últimos detalhes de decoração." },
          { title: "Curadoria e coordenação de fornecedores", description: "Seleção criteriosa, negociação e gestão de toda a equipa que fará parte do vosso dia." },
          { title: "Design e visão criativa", description: "Desenvolvimento de um conceito único que traduza a vossa história em cada detalhe visual." },
          { title: "Gestão de orçamento detalhada", description: "Controlo rigoroso de custos, negociação de valores e transparência total ao longo do processo." },
          { title: "Coordenação total do dia do casamento", description: "Direção de toda a operação no dia, para que só tenham de se preocupar em ser felizes." },
          { title: "Acompanhamento personalizado e próximo", description: "Disponibilidade contínua, comunicação fluida e uma relação de confiança genuína." }
        ],
        ideal: "Ideal para casais que desejam viver o planeamento e o dia do casamento com tranquilidade absoluta, sabendo que cada detalhe está em boas mãos."
      }
    ],
    celebrante: {
      label: 'Cerimónias com Alma',
      title: 'Serviço de Celebrante',
      subtitle: 'Uma cerimónia que conta a vossa história',
      description: 'Há momentos que merecem mais do que palavras genéricas. A vossa cerimónia deve ser tão única quanto a vossa história de amor. Como celebrante, crio cerimónias simbólicas profundamente personalizadas, onde cada palavra, cada ritual e cada momento é pensado exclusivamente para vocês.',
      features: [
        'Cerimónias simbólicas únicas e personalizadas',
        'Escrita exclusiva baseada na vossa história',
        'Rituais com significado autêntico',
        'Tom emocional, verdadeiro e íntimo',
        'Disponível de forma independente ou integrado no planeamento'
      ],
      cta: 'Descobrir mais sobre cerimónias',
      note: 'O serviço de celebrante pode ser contratado de forma independente, mesmo que não tenham wedding planner.'
    }
  },
  en: {
    sectionLabel: 'Wedding Planner',
    sectionTitle: 'Planning Services',
    sectionSubtitle: 'Every wedding deserves tailored support. Choose the level of involvement that makes sense for you.',
    learnMore: 'Learn more',
    idealFor: 'Ideal for',
    mostChosen: 'Most Popular',
    services: [
      {
        id: 1,
        title: "Essence",
        packType: "Essential Pack",
        description: "For couples who like to take the reins of planning but value the security of professional guidance. A solid starting point to begin this journey with confidence.",
        icon: "MessageCircle",
        features: [
          { title: "Personalized initial consultation", description: "A dedicated session to understand your vision, answer questions and map out the planning journey." },
          { title: "General planning guidance", description: "Guidelines on timings, priorities and fundamental decisions for your wedding." },
          { title: "Basic organization support", description: "Tools and structure to keep everything organized throughout the process." },
          { title: "Recommended suppliers list", description: "Access to trusted contacts suited to your style and budget." }
        ],
        ideal: "Ideal for independent couples who want to plan at their own pace, with the peace of mind of having professional support when needed."
      },
      {
        id: 2,
        title: "Harmony",
        packType: "Intermediate Pack",
        description: "Closer support for couples who wish to share the planning process. I'll be by your side for important decisions, ensuring each choice reflects who you are.",
        icon: "Clock",
        features: [
          { title: "Structured and phased planning", description: "Complete process organization with clear stages, defined deadlines and regular follow-up." },
          { title: "Supplier selection support", description: "Attendance at supplier meetings, proposal analysis and guidance on choices." },
          { title: "Timing and priority management", description: "Active follow-up to ensure nothing is forgotten and everything happens at the right time." },
          { title: "Regular follow-up meetings", description: "Regular check-ins to adjust details and keep planning on track." },
          { title: "Close support in final weeks", description: "More intensive presence as the big day approaches, so you arrive calm at the altar." }
        ],
        ideal: "Ideal for couples who want to be involved in planning but prefer to have someone they trust walking alongside them."
      },
      {
        id: 3,
        title: "Fullness",
        packType: "Complete Pack",
        description: "The complete experience of having a wedding planner dedicated to your wedding. From first sketch to the last smile of the night, I take care of everything so you can live each moment with a light heart.",
        icon: "Calendar",
        features: [
          { title: "Complete wedding planning", description: "Full management of all elements, from venue choice to the final decoration details." },
          { title: "Supplier curation and coordination", description: "Careful selection, negotiation and management of the entire team for your day." },
          { title: "Design and creative vision", description: "Development of a unique concept that translates your story into every visual detail." },
          { title: "Detailed budget management", description: "Rigorous cost control, value negotiation and total transparency throughout the process." },
          { title: "Full wedding day coordination", description: "Direction of all operations on the day, so you only need to focus on being happy." },
          { title: "Personalized and close support", description: "Continuous availability, fluid communication and a genuine relationship of trust." }
        ],
        ideal: "Ideal for couples who want to experience planning and the wedding day with absolute tranquility, knowing every detail is in good hands."
      }
    ],
    celebrante: {
      label: 'Ceremonies with Soul',
      title: 'Celebrant Service',
      subtitle: 'A ceremony that tells your story',
      description: 'Some moments deserve more than generic words. Your ceremony should be as unique as your love story. As a celebrant, I create deeply personalized symbolic ceremonies, where every word, every ritual and every moment is designed exclusively for you.',
      features: [
        'Unique and personalized symbolic ceremonies',
        'Exclusive writing based on your story',
        'Rituals with authentic meaning',
        'Emotional, true and intimate tone',
        'Available independently or integrated with planning'
      ],
      cta: 'Discover more about ceremonies',
      note: 'The celebrant service can be hired independently, even if you don\'t have a wedding planner.'
    }
  },
  fr: {
    sectionLabel: 'Wedding Planner',
    sectionTitle: 'Services de Planification',
    sectionSubtitle: 'Chaque mariage mérite un accompagnement sur mesure. Choisissez le niveau d\'implication qui vous convient.',
    learnMore: 'En savoir plus',
    idealFor: 'Pour qui',
    mostChosen: 'Le Plus Choisi',
    services: [
      {
        id: 1,
        title: "Essence",
        packType: "Pack Essentiel",
        description: "Pour les couples qui aiment garder les rênes de la planification mais apprécient la sécurité d'une orientation professionnelle. Un point de départ solide pour commencer ce voyage en confiance.",
        icon: "MessageCircle",
        features: [
          { title: "Consultation initiale personnalisée", description: "Une séance dédiée pour comprendre votre vision, répondre aux questions et tracer le chemin de la planification." },
          { title: "Orientation générale de planification", description: "Lignes directrices sur les délais, les priorités et les décisions fondamentales pour votre mariage." },
          { title: "Soutien à l'organisation de base", description: "Outils et structure pour tout garder organisé tout au long du processus." },
          { title: "Liste de fournisseurs recommandés", description: "Accès à des contacts de confiance adaptés à votre style et budget." }
        ],
        ideal: "Idéal pour les couples autonomes qui veulent planifier à leur rythme, avec la tranquillité d'esprit d'avoir un soutien professionnel si nécessaire."
      },
      {
        id: 2,
        title: "Harmonie",
        packType: "Pack Intermédiaire",
        description: "Un accompagnement plus proche pour les couples qui souhaitent partager le processus de planification. Je serai à vos côtés pour les décisions importantes, en veillant à ce que chaque choix vous reflète.",
        icon: "Clock",
        features: [
          { title: "Planification structurée et phasée", description: "Organisation complète du processus avec des étapes claires, des délais définis et un suivi régulier." },
          { title: "Aide à la sélection des fournisseurs", description: "Présence aux réunions avec les fournisseurs, analyse des propositions et orientation des choix." },
          { title: "Gestion des délais et des priorités", description: "Suivi actif pour s'assurer que rien n'est oublié et que tout se passe au bon moment." },
          { title: "Réunions de suivi régulières", description: "Points réguliers pour ajuster les détails et maintenir la planification sur la bonne voie." },
          { title: "Soutien rapproché les dernières semaines", description: "Présence plus intensive à l'approche du grand jour, pour arriver serein à l'autel." }
        ],
        ideal: "Idéal pour les couples qui veulent être impliqués dans la planification mais préfèrent avoir quelqu'un de confiance à leurs côtés."
      },
      {
        id: 3,
        title: "Plénitude",
        packType: "Pack Complet",
        description: "L'expérience complète d'avoir une wedding planner dédiée à votre mariage. De la première esquisse au dernier sourire de la soirée, je m'occupe de tout pour que vous viviez chaque moment le cœur léger.",
        icon: "Calendar",
        features: [
          { title: "Planification complète du mariage", description: "Gestion intégrale de tous les éléments, du choix du lieu aux derniers détails de décoration." },
          { title: "Curation et coordination des fournisseurs", description: "Sélection soignée, négociation et gestion de toute l'équipe pour votre jour." },
          { title: "Design et vision créative", description: "Développement d'un concept unique qui traduit votre histoire dans chaque détail visuel." },
          { title: "Gestion détaillée du budget", description: "Contrôle rigoureux des coûts, négociation des valeurs et transparence totale tout au long du processus." },
          { title: "Coordination totale le jour du mariage", description: "Direction de toutes les opérations le jour J, pour que vous n'ayez qu'à être heureux." },
          { title: "Accompagnement personnalisé et proche", description: "Disponibilité continue, communication fluide et relation de confiance authentique." }
        ],
        ideal: "Idéal pour les couples qui souhaitent vivre la planification et le jour du mariage en toute sérénité, sachant que chaque détail est entre bonnes mains."
      }
    ],
    celebrante: {
      label: 'Cérémonies avec Âme',
      title: 'Service de Célébrant',
      subtitle: 'Une cérémonie qui raconte votre histoire',
      description: 'Certains moments méritent plus que des mots génériques. Votre cérémonie doit être aussi unique que votre histoire d\'amour. En tant que célébrant, je crée des cérémonies symboliques profondément personnalisées, où chaque mot, chaque rituel et chaque moment est conçu exclusivement pour vous.',
      features: [
        'Cérémonies symboliques uniques et personnalisées',
        'Écriture exclusive basée sur votre histoire',
        'Rituels avec une signification authentique',
        'Ton émotionnel, vrai et intime',
        'Disponible indépendamment ou intégré à la planification'
      ],
      cta: 'Découvrir les cérémonies',
      note: 'Le service de célébrant peut être réservé indépendamment, même sans wedding planner.'
    }
  },
  es: {
    sectionLabel: 'Wedding Planner',
    sectionTitle: 'Servicios de Planificación',
    sectionSubtitle: 'Cada boda merece un acompañamiento a su medida. Elige el nivel de participación que tenga sentido para vosotros.',
    learnMore: 'Saber más',
    idealFor: 'Para quién es',
    mostChosen: 'Más Elegido',
    services: [
      {
        id: 1,
        title: "Esencia",
        packType: "Pack Esencial",
        description: "Para parejas que les gusta tener las riendas de la planificación pero valoran la seguridad de una orientación profesional. Un punto de partida sólido para comenzar este viaje con confianza.",
        icon: "MessageCircle",
        features: [
          { title: "Consultoría inicial personalizada", description: "Una sesión dedicada a conocer vuestra visión, responder dudas y trazar el camino de la planificación." },
          { title: "Orientación general en la planificación", description: "Líneas orientadoras sobre tiempos, prioridades y decisiones fundamentales para vuestra boda." },
          { title: "Apoyo en la organización base", description: "Herramientas y estructura para mantener todo organizado a lo largo del proceso." },
          { title: "Lista de proveedores recomendados", description: "Acceso a contactos de confianza adecuados a vuestro estilo y presupuesto." }
        ],
        ideal: "Ideal para parejas autónomas que quieren planificar a su ritmo, pero con la tranquilidad de saber que tienen apoyo profesional cuando lo necesiten."
      },
      {
        id: 2,
        title: "Armonía",
        packType: "Pack Intermedio",
        description: "Un acompañamiento más cercano para parejas que desean compartir el proceso de planificación. Estaré a vuestro lado en las decisiones importantes, garantizando que cada elección refleje quiénes sois.",
        icon: "Clock",
        features: [
          { title: "Planificación estructurada y por fases", description: "Organización completa del proceso con etapas claras, plazos definidos y seguimiento regular." },
          { title: "Apoyo en la selección de proveedores", description: "Presencia en las reuniones con proveedores, análisis de propuestas y orientación en las elecciones." },
          { title: "Gestión de tiempos y prioridades", description: "Seguimiento activo para garantizar que nada se olvide y todo suceda en el momento adecuado." },
          { title: "Reuniones de seguimiento periódicas", description: "Puntos de situación regulares para ajustar detalles y mantener la planificación en el rumbo correcto." },
          { title: "Apoyo cercano en las semanas finales", description: "Presencia más intensa a medida que se acerca el gran día, para que lleguéis tranquilos al altar." }
        ],
        ideal: "Ideal para parejas que quieren estar involucradas en la planificación, pero prefieren tener a alguien de confianza caminando a su lado."
      },
      {
        id: 3,
        title: "Plenitud",
        packType: "Pack Completo",
        description: "La experiencia integral de tener una wedding planner dedicada a vuestra boda. Desde el primer boceto hasta la última sonrisa de la noche, me encargo de todo para que viváis cada momento con el corazón ligero.",
        icon: "Calendar",
        features: [
          { title: "Planificación completa de la boda", description: "Gestión integral de todos los elementos, desde la elección del espacio hasta los últimos detalles de decoración." },
          { title: "Curaduría y coordinación de proveedores", description: "Selección cuidadosa, negociación y gestión de todo el equipo que formará parte de vuestro día." },
          { title: "Diseño y visión creativa", description: "Desarrollo de un concepto único que traduzca vuestra historia en cada detalle visual." },
          { title: "Gestión de presupuesto detallada", description: "Control riguroso de costes, negociación de valores y transparencia total a lo largo del proceso." },
          { title: "Coordinación total del día de la boda", description: "Dirección de toda la operación en el día, para que solo tengáis que preocuparos de ser felices." },
          { title: "Acompañamiento personalizado y cercano", description: "Disponibilidad continua, comunicación fluida y una relación de confianza genuina." }
        ],
        ideal: "Ideal para parejas que desean vivir la planificación y el día de la boda con tranquilidad absoluta, sabiendo que cada detalle está en buenas manos."
      }
    ],
    celebrante: {
      label: 'Ceremonias con Alma',
      title: 'Servicio de Celebrante',
      subtitle: 'Una ceremonia que cuenta vuestra historia',
      description: 'Hay momentos que merecen más que palabras genéricas. Vuestra ceremonia debe ser tan única como vuestra historia de amor. Como celebrante, creo ceremonias simbólicas profundamente personalizadas, donde cada palabra, cada ritual y cada momento está pensado exclusivamente para vosotros.',
      features: [
        'Ceremonias simbólicas únicas y personalizadas',
        'Escritura exclusiva basada en vuestra historia',
        'Rituales con significado auténtico',
        'Tono emocional, verdadero e íntimo',
        'Disponible de forma independiente o integrado en la planificación'
      ],
      cta: 'Descubrir más sobre ceremonias',
      note: 'El servicio de celebrante puede contratarse de forma independiente, incluso sin wedding planner.'
    }
  }
};

const ServicesSection = () => {
  const [selectedService, setSelectedService] = useState(null);
  const { currentLanguage } = useLanguage();

  // Get content for current language, fallback to Portuguese
  const c = servicesContent[currentLanguage] || servicesContent.pt;
  const displayServices = c.services;

  return (
    <section id="services" className="section-padding bg-transparent">
      <div className="max-w-7xl mx-auto">
        
        {/* ==================== WEDDING PLANNER SERVICES ==================== */}
        <div className="mb-24">
          {/* Section Header */}
          <div className="text-center max-w-3xl mx-auto mb-16">
            <p className="text-caption text-[#C4B5A0] mb-4">
              {c.sectionLabel}
            </p>
            <h2 className="heading-secondary text-4xl md:text-5xl text-[#2C2C2C] mb-4">
              {c.sectionTitle}
            </h2>
            <div className="divider" />
            <p className="text-body text-[#6B4E37] max-w-2xl mx-auto">
              {c.sectionSubtitle}
            </p>
          </div>
          
          {/* Services Grid - 3 Packs */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
            {displayServices.map((service, index) => {
              const IconComponent = iconMap[service.icon];
              const isHighlighted = index === 2; // Highlight the "Plenitude" pack
              
              return (
                <div
                  key={service.id}
                  className={`relative rounded-2xl p-8 transition-all duration-300 ${
                    isHighlighted 
                      ? 'bg-gradient-to-b from-[#2C2C2C] to-[#3d3d3d] text-white shadow-2xl lg:scale-105' 
                      : 'bg-white/80 backdrop-blur-sm border border-[#E8E4E0] hover:shadow-xl hover:border-[#C4B5A0]'
                  }`}
                  style={{ animationDelay: `${index * 100}ms` }}
                  data-testid={`service-card-${service.id}`}
                >
                  {/* Popular Badge for highlighted */}
                  {isHighlighted && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <span className="bg-[#C4B5A0] text-[#2C2C2C] text-xs font-medium px-4 py-1.5 rounded-full">
                        {c.mostChosen}
                      </span>
                    </div>
                  )}

                  {/* Pack Type Label */}
                  <p className={`text-xs font-medium tracking-wider uppercase mb-3 ${
                    isHighlighted ? 'text-[#C4B5A0]' : 'text-[#C4B5A0]'
                  }`}>
                    {service.packType}
                  </p>
                  
                  {/* Title with Icon */}
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      isHighlighted ? 'bg-[#C4B5A0]/20' : 'bg-[#D4C4B0]/30'
                    }`}>
                      {IconComponent && <IconComponent size={20} className={isHighlighted ? 'text-[#C4B5A0]' : 'text-[#6B4E37]'} />}
                    </div>
                    <h3 className={`font-['Cormorant_Garamond'] text-2xl ${
                      isHighlighted ? 'text-white' : 'text-[#2C2C2C]'
                    }`}>
                      {service.title}
                    </h3>
                  </div>
                  
                  {/* Description */}
                  <p className={`text-sm leading-relaxed mb-6 ${
                    isHighlighted ? 'text-gray-300' : 'text-[#8B7D6B]'
                  }`}>
                    {service.description}
                  </p>
                  
                  {/* Features List */}
                  <ul className="space-y-3 mb-6">
                    {service.features.map((feature, featureIndex) => (
                      <li
                        key={featureIndex}
                        className={`flex items-start gap-3 text-sm ${
                          isHighlighted ? 'text-gray-200' : 'text-[#6B4E37]'
                        }`}
                      >
                        <Check size={16} className={`mt-0.5 flex-shrink-0 ${
                          isHighlighted ? 'text-[#C4B5A0]' : 'text-[#B5C4B1]'
                        }`} />
                        <span>{feature.title}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Ideal For */}
                  {service.ideal && (
                    <div className={`text-xs italic pt-4 border-t ${
                      isHighlighted ? 'border-gray-600 text-gray-400' : 'border-[#E8E4E0] text-[#8B7D6B]'
                    }`}>
                      <span className="font-medium">{c.idealFor}:</span> {service.ideal}
                    </div>
                  )}

                  {/* Learn More Button */}
                  <button
                    onClick={() => setSelectedService(service)}
                    className={`w-full mt-6 flex items-center justify-center gap-2 py-3 px-4 rounded-lg font-medium text-sm transition-all ${
                      isHighlighted 
                        ? 'bg-[#C4B5A0] text-[#2C2C2C] hover:bg-[#D4C4B0]' 
                        : 'border border-[#C4B5A0] text-[#6B4E37] hover:bg-[#C4B5A0]/10'
                    }`}
                    data-testid={`service-details-${service.id}`}
                  >
                    {c.learnMore}
                    <ArrowRight size={16} />
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {/* ==================== CELEBRANTE SERVICE ==================== */}
        <div className="relative">
          <div className="absolute inset-0 bg-gradient-to-r from-[#D4C4B0]/10 via-[#B5C4B1]/10 to-[#D4C4B0]/10 rounded-3xl" />
          
          <div className="relative rounded-3xl border border-[#E8E4E0] bg-white/60 backdrop-blur-sm overflow-hidden">
            <div className="grid lg:grid-cols-2 gap-0">
              {/* Left Content */}
              <div className="p-10 lg:p-14">
                <div className="flex items-center gap-2 mb-4">
                  <Sparkles size={18} className="text-[#C4B5A0]" />
                  <p className="text-caption text-[#C4B5A0]">
                    {c.celebrante.label}
                  </p>
                </div>
                
                <h2 className="font-['Cormorant_Garamond'] text-3xl lg:text-4xl text-[#2C2C2C] mb-3">
                  {c.celebrante.title}
                </h2>
                
                <p className="font-['Cormorant_Garamond'] text-xl text-[#6B4E37] italic mb-6">
                  {c.celebrante.subtitle}
                </p>
                
                <p className="text-[#8B7D6B] leading-relaxed mb-8">
                  {c.celebrante.description}
                </p>
                
                {/* Features */}
                <ul className="space-y-3 mb-8">
                  {c.celebrante.features.map((feature, index) => (
                    <li key={index} className="flex items-start gap-3 text-sm text-[#6B4E37]">
                      <Check size={16} className="mt-0.5 flex-shrink-0 text-[#B5C4B1]" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                {/* Note */}
                <p className="text-xs text-[#8B7D6B] italic mb-8 p-4 bg-[#FAF9F7] rounded-lg">
                  {c.celebrante.note}
                </p>
                
                {/* CTA */}
                <Link
                  to="/celebrante"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-[#2C2C2C] text-white rounded-lg font-medium text-sm hover:bg-[#3d3d3d] transition-colors"
                >
                  {c.celebrante.cta}
                  <ArrowRight size={16} />
                </Link>
              </div>
              
              {/* Right Image */}
              <div className="relative h-64 lg:h-auto">
                <img
                  src="https://images.pexels.com/photos/2253870/pexels-photo-2253870.jpeg?auto=compress&cs=tinysrgb&w=800"
                  alt="Cerimónia de casamento"
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-white/40 via-transparent to-transparent lg:from-white/60" />
              </div>
            </div>
          </div>
        </div>

      </div>

      {/* Service Detail Modal */}
      <ServiceDetailModal
        isOpen={selectedService !== null}
        onClose={() => setSelectedService(null)}
        service={selectedService}
      />
    </section>
  );
};

export default ServicesSection;
